import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { NonDigitalDataType, NonDigitalValueFlag, ReposeGroup } from 'icreate/common/models/repose-group';
import { firstValueFrom, lastValueFrom, take } from 'rxjs';
import { ReposeDetailDialogComponent } from '../repose-detail-dialog/repose-detail-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { defaultAlarmDropDownItem } from '@models/repose-group';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import _ from 'lodash';
import { ReposeDialogComponent, ReposeGroupStep } from '../repose-dialog/repose-dialog.component';
import { ReposeGroupHelperService } from '@service/repose-group-helper.service';

@Component({
    selector: 'app-repose-row',
    templateUrl: './repose-row.component.html',
    styleUrls: ['./repose-row.component.css'],
    standalone: false,
})
export class ReposeRowComponent {
    @Input() reposeGroup: any;
    @Input() reposeGroupEditMode: boolean = false;
    @Input() index: number;
    @Input() reposeGroupList: ReposeGroup[] = [];
    @Input() userCanEdit: boolean = true;
    @Output() onDeleteRow: EventEmitter<void> = new EventEmitter<void>();
    @Output() onRowUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('optionsIcon') optionsIcon: ElementRef;

    channel: Channel;
    optionsVisible: boolean;
    isInfoMessageVisible: boolean = false;

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.optionsIcon?.nativeElement.contains(event.target) && this.optionsVisible) {
            this.optionsVisible = false;
        }
    }

    constructor(
        private channelService: ChannelService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private reposeGroupHelperService: ReposeGroupHelperService
    ) {}

    async ngOnInit() {
        this.channel = await lastValueFrom(this.channelService.getById(this.reposeGroup.channelId));
    }

    ngAfterViewInit() {
        document.addEventListener(
            'scroll',
            () => {
                if (this.optionsVisible) {
                    this.optionsVisible = false;
                }
            },
            true
        );
    }

    getChannelName() {
        if (this.channel) return String(Number(this.channel?.opsData.tag)) + ' ' + this.channel?.opsData.name;
    }

    getTriggerCondition() {
        if (this.reposeGroup.nonDigitalDataType === NonDigitalDataType.VALUE) {
            let message;
            if (this.reposeGroup.nonDigitalValueFlag === NonDigitalValueFlag.EXCEED) {
                message = this.translate.instant('repose-group.dialog.digital.set-point.exceeds');
            } else {
                message = this.translate.instant('repose-group.dialog.digital.set-point.below');
            }
            return message;
        } else if (this.reposeGroup.nonDigitalDataType === NonDigitalDataType.STATUS) {
            const findItem = defaultAlarmDropDownItem.find(
                item => item.propertyName === this.reposeGroup.nonDigitalAlarm
            );

            let itemName = this.translate.instant(findItem.name);
            let message = this.translate.instant('repose-group.dialog.digital.set-point.reaches');

            return message + ' ' + itemName;
        } else if (this.reposeGroup.digitalChannelStatus == 0 || this.reposeGroup.digitalChannelStatus == 1) {
            let message = this.translate.instant('repose-group.dialog.digital.set-point.message');

            return message + ' ';
        }
    }

    getValue() {
        if (this.reposeGroup.nonDigitalDataType === NonDigitalDataType.VALUE) {
            return this.reposeGroup.setPoint + ' ' + this.channel?.opsData.measurementUnit;
        } else if (this.reposeGroup.nonDigitalDataType === NonDigitalDataType.STATUS) {
            let setPoint = this.channel?.fcuData[this.reposeGroup.nonDigitalAlarm]?.setPoint;
            return setPoint ? setPoint + ' ' + this.channel?.opsData.measurementUnit : '';
        } else if (this.reposeGroup.digitalChannelStatus == 0) {
            return this.channel?.fcuData.digitalAlarmSetting.statusStringContactOff;
        } else if (this.reposeGroup.digitalChannelStatus == 1) {
            return this.channel?.fcuData.digitalAlarmSetting.statusStringContactOn;
        }
    }

    getDelayString(delay) {
        if (!delay) return this.translate.instant('repose-group.page.table.header.no-delay');
        return delay + ' sec';
    }

    getDescription() {
        let message = this.translate.instant('repose-group.page.no-description-message');

        return this.reposeGroup.description ? this.reposeGroup.description : message;
    }

    async editReposeGroup() {
        if (this.reposeGroupEditMode) {
            return;
        }
        const dialogRef = this.dialog.open(ReposeDetailDialogComponent, {
            data: {
                reposeGroup: this.reposeGroup,
                channel: this.channel,
                reposeGroupList: this.reposeGroupList,
            },
            panelClass: 'repose-dialog',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                Object.assign(this.reposeGroup, result.reposeGroup);
                this.channel = result.channel;
                this.rowUpdated();
            }
        });
    }

    async openSearchChannelDialog(event: MouseEvent) {
        event.stopPropagation();

        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.reposeGroup.vesselId,
                assignedChannel: this.channel,
                allowedTypesSet: undefined,
                setShouldDisableTypes: false,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (channelSelected) {
            const newGroup = {
                ...this.reposeGroup,
                channelId: channelSelected.id,
            };

            const dialogRef = this.dialog.open(ReposeDialogComponent, {
                data: {
                    ...newGroup,
                    currentStep: ReposeGroupStep.STEP2,
                    reposeGroupList: this.reposeGroupList,
                    disableGoBack: true,
                },
            });

            const result = await firstValueFrom(dialogRef.afterClosed());

            if (result == 'close') {
                this.editReposeGroup();
            } else if (result) {
                this.resetTriggerChannel();
                Object.assign(this.reposeGroup, result.reposeGroup);
                this.channel = result.channel;
                this.rowUpdated();
            }
        }
    }

    resetTriggerChannel() {
        this.channel = null;
        this.reposeGroup.channelId = null;
        this.reposeGroup.nonDigitalDataType = NonDigitalDataType.NULL;
        this.reposeGroup.nonDigitalValueFlag = NonDigitalValueFlag.NULL;
        this.reposeGroup.setPoint = '';
        this.reposeGroup.nonDigitalAlarm = '';
        this.reposeGroup.digitalChannelStatus = '';
        this.reposeGroup.startDelay = null;
        this.reposeGroup.stopDelay = null;
        this.rowUpdated();
    }

    openOptions() {
        this.optionsVisible = !this.optionsVisible;
    }

    deleteRow() {
        this.onDeleteRow.emit();
        this.optionsVisible = false;
    }

    rowUpdated() {
        this.reposeGroupHelperService.groupUpdated();
    }
}
